import React, { useEffect, useRef, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import { TranslateFunction } from 'react-localize-redux';

import { flagService } from '../../../../core/services';
import { domUtils, utils } from '../../../../utils';
import { Currency } from '../../../../weborder/Api';
import { CustomTip } from './CustomTip';
import { CustomTipOption } from './CustomTipOption';
import { TippingOption, TippingValue } from './TippingOption';

const useStyles = makeStyles((theme: Theme) => ({
  roundUpDiv: {
    display: 'flex',
    height: 70,
    marginLeft: theme.spacing(1),
    minWidth: 93,
  },
  tippingOptionDiv: {
    display: 'flex',
    height: 70,
    marginLeft: theme.spacing(1),
    minWidth: 85,
  },
  tippingRow: {
    display: 'flex',
    flexFlow: 'row  nowrap',
    overflowX: 'auto',
    padding: theme.spacing(1, 0),
    '& :first-child': {
      marginLeft: 0,
    },
  },
  tippingOption: {
    flex: 1,
    '&.Mui-selected': {
      background: theme.palette.common.white,
      border: theme.palette.primary.main,
      boxShadow: `inset 0px 0px 0px 2px ${theme.palette.primary.main}`,
    },
    '& .fd-text': {
      fontSize: theme.typography.pxToRem(14),
    },
  },
}));

export type TippingProps = {
  currency: Currency;
  customTipAmountLabel: string;
  hasCustomTip: boolean;
  isCustomTipEnabled: boolean;
  isRoundUpEnabled: boolean;
  onChange: (tip: number) => void;
  onHasCustomTipChange: (hasCustomTip: boolean) => void;
  orderTotal: number;
  roundUpAmount: string;
  selectedTipValue: number;
  showRoundUp: boolean;
  tippingOptions: TippingValue[];
  toggleRoundUp: () => void;
  translate: TranslateFunction;
};

export function Tipping(props: TippingProps): JSX.Element {
  const {
    currency,
    customTipAmountLabel,
    hasCustomTip,
    isCustomTipEnabled,
    isRoundUpEnabled,
    onChange,
    onHasCustomTipChange,
    orderTotal,
    roundUpAmount,
    selectedTipValue,
    showRoundUp,
    tippingOptions,
    toggleRoundUp,
    translate,
  } = props;
  const classes = useStyles();

  const [showCustomTipInput, setShowCustomTipInput] = useState<boolean>(false);
  const [customTipAmount, setCustomTipAmount] = useState<string>('');

  const customTipRef = useRef<null | HTMLDivElement>(null);

  const enableCustomTipFlag = flagService.isFlagOn('enableCustomTip');

  const isDecimal = utils.check.isDecimalCurrency(currency);
  const integerRegex = /\/^d*$\//;
  const floatRegex = /^(\d*\.?\d{0,2}$)/;

  useEffect(() => {
    if (hasCustomTip) calculateCustomTipPercent(parseFloat(customTipAmount));
  }, [orderTotal]);

  useEffect(() => {
    if (!showCustomTipInput && hasCustomTip) {
      domUtils.ensureVisible(customTipRef.current);
    }
  }, [showCustomTipInput, hasCustomTip]);

  const toggleCustomTipInput = (): void => {
    setShowCustomTipInput(!showCustomTipInput);
  };

  const handleTippingOptionChange = (tip: number): void => {
    if (hasCustomTip) {
      setCustomTipAmount('');
      onHasCustomTipChange(false);
    }
    onChange(tip);
  };

  const handleCustomTipChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const validated = isDecimal
      ? event.target.value.match(floatRegex)
      : event.target.value.match(integerRegex);
    if (validated) {
      setCustomTipAmount(event.target.value);
    }
  };

  const submitCustomTip = (): void => {
    const tipAmount = parseFloat(customTipAmount);
    if (!Number.isNaN(tipAmount)) {
      calculateCustomTipPercent(tipAmount);
    } else {
      calculateCustomTipPercent(0);
    }
    toggleCustomTipInput();
  };

  const calculateCustomTipPercent = (customTip: number): void => {
    const tipPercent = (100 * customTip) / orderTotal;
    onChange(tipPercent);
    onHasCustomTipChange(true);

    if (isRoundUpEnabled) {
      toggleRoundUp();
    }
  };

  const isTippingOptionSelected = (tippingOption: TippingValue): boolean => {
    if (isRoundUpEnabled) {
      return false;
    }

    return selectedTipValue === parseInt(tippingOption.value) && !hasCustomTip;
  };

  const handleOnClickTippingOption = (tippingOption: TippingValue): void => {
    if (isRoundUpEnabled) {
      toggleRoundUp();
    }
    handleTippingOptionChange(parseFloat(tippingOption.value));
  };

  const handleOnClickRoundUpTippingOption = (): void => {
    onChange(0);
    onHasCustomTipChange(false);
    toggleRoundUp();
  };

  const roundUpTippingOption: TippingValue = {
    subValue: roundUpAmount,
    value: translate('Tipping_RoundUp') as string,
  };

  const renderTippingRow = (): JSX.Element => (
    <div className={classes.tippingRow}>
      {showRoundUp && (
        <div className={classes.roundUpDiv}>
          <TippingOption
            className={classes.tippingOption}
            onClick={handleOnClickRoundUpTippingOption}
            selected={isRoundUpEnabled && !hasCustomTip}
            tippingOption={roundUpTippingOption}
          />
        </div>
      )}
      {tippingOptions.map((tippingOption) => (
        <div className={classes.tippingOptionDiv} key={tippingOption.value}>
          <TippingOption
            className={classes.tippingOption}
            onClick={() => handleOnClickTippingOption(tippingOption)}
            selected={isTippingOptionSelected(tippingOption)}
            tippingOption={tippingOption}
          />
        </div>
      ))}
      {isCustomTipEnabled && enableCustomTipFlag && (
        <div className={classes.tippingOptionDiv} ref={customTipRef}>
          <CustomTipOption
            className={classes.tippingOption}
            customTipAmountLabel={customTipAmountLabel}
            hasCustomTip={hasCustomTip}
            toggleCustomTipInput={toggleCustomTipInput}
            translate={translate}
          />
        </div>
      )}
    </div>
  );

  return (
    <div>
      {showCustomTipInput ? (
        <CustomTip
          currency={currency}
          customTipAmount={customTipAmount}
          isDecimal={isDecimal}
          onChange={handleCustomTipChange}
          onSubmit={submitCustomTip}
          translate={translate}
        />
      ) : (
        renderTippingRow()
      )}
    </div>
  );
}
